import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

interface PushBatch {
  batch_id: string;
  target: string;
  title: string;
  body: string;
  url: string;
  recipients: number;
  sent: number;
  failed: number;
  sent_at: string;
  names: string[] | null;
}

function when(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Karachi',
  });
}

// Guest targets are raw device UUIDs; show enough to identify, not the lot.
function shortTarget(t: string): string {
  if (t.toLowerCase() === 'all') return 'Everyone';
  if (t.endsWith('@device.gardenoai.com')) return `Guest ${t.slice(0, 8).toUpperCase()}`;
  return t;
}

function PushLog({ token, isMobile, refreshKey }: { token: string; isMobile: boolean; refreshKey?: number }) {
  const [rows, setRows] = useState<PushBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/push-log?limit=50`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setRows(((d.data ?? d).pushes ?? []) as PushBatch[]);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const th: React.CSSProperties = {
    textAlign: 'left', padding: '11px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const td: React.CSSProperties = {
    textAlign: 'left', padding: '11px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, verticalAlign: 'top',
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: C.text }}>Sent notifications</h2>
        <button
          onClick={load}
          style={{ marginLeft: 'auto', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: C.text, fontSize: 13 }}
        >
          Refresh
        </button>
      </div>
      <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 12px' }}>
        Every push sent from this panel, newest first. Care reminders scheduled on the device are not
        listed here, they appear in the reminder funnel above.
      </p>

      {error && <div style={{ color: '#F87171', fontSize: 13.5, marginBottom: 10 }}>{error}</div>}
      {loading && rows.length === 0 && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
          <thead>
            <tr>
              <th style={th}>Sent</th>
              <th style={th}>Notification</th>
              <th style={th}>To</th>
              <th style={{ ...th, textAlign: 'right' }}>Delivered</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.batch_id}>
                <td style={{ ...td, whiteSpace: 'nowrap', color: C.sub, fontSize: 12.5 }}>{when(p.sent_at)}</td>
                <td style={{ ...td, maxWidth: 380 }}>
                  <div style={{ fontWeight: 700 }}>{p.title}</div>
                  <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.5 }}>{p.body}</div>
                  {p.url && <div style={{ fontSize: 11.5, color: C.accent, marginTop: 3 }}>{p.url}</div>}
                </td>
                <td style={{ ...td, fontSize: 12.5 }}>
                  {p.names && p.names.length > 0
                    ? <span>{p.names.join(', ')}{p.recipients > p.names.length ? ` +${p.recipients - p.names.length}` : ''}</span>
                    : <span style={{ color: C.sub }}>{shortTarget(p.target)}</span>}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <span style={{ color: p.sent > 0 ? '#34D399' : C.sub, fontWeight: 700 }}>{p.sent}</span>
                  {p.failed > 0 && <span style={{ color: '#F87171' }}> / {p.failed} failed</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: C.sub }}>No pushes sent yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PushLog;
